import React, { useState, useEffect, useCallback } from 'react';
import ProjectCard from './ProjectCard';
import Icon from '../../../components/AppIcon';

const ProjectGrid = ({ 
  projects, 
  loading, 
  hasMore, 
  onLoadMore, 
  onImageClick, 
  onBookService 
}) => {
  const [loadingMore, setLoadingMore] = useState(false);

  const handleLoadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    
    setLoadingMore(true);
    await onLoadMore();
    setLoadingMore(false);
  }, [loadingMore, hasMore, onLoadMore]);

  // Infinite scroll implementation
  useEffect(() => {
    const handleScroll = () => {
      if (
        window.innerHeight + document.documentElement?.scrollTop
        >= document.documentElement?.offsetHeight - 1000
      ) {
        handleLoadMore();
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleLoadMore]);

  // Skeleton loader component
  const SkeletonCard = () => (
    <div className="bg-white rounded-lg shadow-elevation-2 overflow-hidden animate-pulse">
      <div className="aspect-[4/3] bg-muted" />
      <div className="p-4">
        <div className="h-4 bg-muted rounded mb-2" />
        <div className="h-3 bg-muted rounded w-3/4 mb-3" />
        <div className="h-3 bg-muted rounded mb-4" />
        <div className="flex space-x-2">
          <div className="h-8 bg-muted rounded flex-1" />
          <div className="h-8 bg-muted rounded flex-1" />
        </div>
      </div>
    </div>
  );

  if (loading && projects?.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 lg:px-6 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(9)]?.map((_, index) => (
            <SkeletonCard key={index} />
          ))}
        </div>
      </div>
    );
  }

  if (!loading && projects?.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 lg:px-6 py-16">
        <div className="text-center">
          <div className="w-24 h-24 bg-muted rounded-full flex items-center justify-center mx-auto mb-6">
            <Icon name="Search" size={32} color="var(--color-text-secondary)" />
          </div>
          <h3 className="text-xl font-semibold text-text-primary mb-2">
            No Projects Found
          </h3>
          <p className="text-text-secondary mb-6 max-w-md mx-auto">
            We couldn't find any projects matching your current filters. 
            Try adjusting your search criteria or browse all projects.
          </p>
          <button
            onClick={() => window.location?.reload()}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors duration-200"
          >
            <Icon name="RotateCcw" size={16} />
            <span>Reset Filters</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 lg:px-6 py-8">
      {/* Masonry Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {projects?.map((project) => (
          <ProjectCard
            key={project?.id}
            project={project}
            onImageClick={onImageClick}
            onBookService={onBookService}
          />
        ))}
      </div>
      {/* Load More Section */}
      {(hasMore || loadingMore) && (
        <div className="mt-12 text-center">
          {loadingMore ? (
            <div className="space-y-4">
              <div className="flex justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
              </div>
              <p className="text-text-secondary">Loading more projects...</p>
            </div>
          ) : (
            <button
              onClick={handleLoadMore}
              className="inline-flex items-center space-x-2 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors duration-200 shadow-elevation-2 hover:shadow-elevation-3"
            >
              <Icon name="Plus" size={16} />
              <span>Load More Projects</span>
            </button>
          )}
        </div>
      )}
      {/* End of Results */}
      {!hasMore && !loadingMore && projects?.length > 0 && (
        <div className="mt-12 text-center">
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-muted rounded-lg text-text-secondary">
            <Icon name="CheckCircle" size={16} />
            <span>You've seen all projects</span>
          </div>
        </div>
      )}
      {/* Back to Top Button */}
      {projects?.length > 6 && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-6 left-6 w-12 h-12 bg-primary text-white rounded-full shadow-elevation-4 hover:shadow-elevation-5 transition-all duration-200 hover:scale-105 z-30"
          aria-label="Back to top"
        >
          <Icon name="ArrowUp" size={20} />
        </button>
      )}
    </div>
  );
};

export default ProjectGrid;